(function () {
    window.NewsGraph = window.NewsGraph || {};

    window.NewsGraph.NARRATIVE_TYPES = new Set(['narrative', 'secondary_narrative', 'ticker_narrative']);

    window.NewsGraph.DIRECTION_COLORS = {
        bullish: 0x00ff00,
        bearish: 0xff0000,
        mixed:   0xffff00,
        neutral: 0xccccdd,
    };


    window.NewsGraph.getColor = function(direction) {
        return window.NewsGraph.DIRECTION_COLORS[direction] || window.NewsGraph.DIRECTION_COLORS.neutral;
    };

    window.NewsGraph.selectNodes = function(data) {
        const rawNarratives = data.narratives || [];
        const rawTickers = data.tickers || [];
        const lists = data.lists || data.rankings || {};

        let narrativePool = rawNarratives;
        const preferredNarrativeIds = Array.isArray(lists.graph_narratives)
            ? lists.graph_narratives
            : lists.market_narratives;
        if (Array.isArray(preferredNarrativeIds)) {
            const nMap = {};
            rawNarratives.forEach(n => { if (n.id) nMap[n.id] = n; });
            narrativePool = preferredNarrativeIds.map(id => nMap[id]).filter(Boolean);
        }

        const narratives = narrativePool
            .map(n => {
                let type = 'narrative';
                if (n.tier === 'secondary') type = 'secondary_narrative';
                if (n.tier === 'ticker') type = 'ticker_narrative';
                return { ...n, type, label: n.short_label || n.title };
            })
            .slice(0, 8);

        let tickerPool = rawTickers;
        const preferredTickerIds = Array.isArray(lists.graph_tickers)
            ? lists.graph_tickers
            : lists.trending_tickers;
        if (Array.isArray(preferredTickerIds)) {
            const tMap = {};
            rawTickers.forEach(t => { if (t.id) tMap[t.id] = t; });
            tickerPool = preferredTickerIds.map(id => tMap[id]).filter(Boolean);
        }

        const tickers = tickerPool
            .map(t => ({
                ...t,
                type: t.is_group ? 'ticker_group' : 'ticker',
                label: t.symbol
            }))
            .slice(0, 15);

        return [...narratives, ...tickers];
    };

    window.NewsGraph.selectLinks = function(relationships, selectedNodes) {
        const ids = new Set(selectedNodes.map(n => n.id));
        const nodeTypeMap = new Map(selectedNodes.map(n => [n.id, n.type]));
        const NARRATIVE_TYPES = window.NewsGraph.NARRATIVE_TYPES;

        return relationships
            .map(r => ({ ...r, source: r.source_id, target: r.target_id }))
            .filter(l => {
                if (!ids.has(l.source) || !ids.has(l.target)) return false;
                if (l.relationship === 'offsets') return false;
                const sourceIsNarrative = NARRATIVE_TYPES.has(nodeTypeMap.get(l.source));
                const targetIsNarrative = NARRATIVE_TYPES.has(nodeTypeMap.get(l.target));
                return sourceIsNarrative || targetIsNarrative;
            });
    };

    window.NewsGraph.nodeRadius = function(articleCount, minC, maxC) {
        const scaleFactor = 1.15;
        if (maxC === minC) return 0.80 * scaleFactor;
        return (0.60 + ((articleCount - minC) / (maxC - minC)) * 0.90) * scaleFactor;
    };

    window.NewsGraph.isPreIpo = function(node, preIpoSymbols) {
        if (!node) return false;
        const symbol = (node.label || node.id || '').split(' - ')[0].trim().toUpperCase();
        return !!(preIpoSymbols && preIpoSymbols.has(symbol));
    };

    window.NewsGraph.isNodeNeutral = function(node, stockMap, preIpoSymbols) {
        const isSphere = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
        if (!isSphere) {
            const symbol = (node.label || node.id || '').split(' - ')[0].trim().toUpperCase();
            const stock = stockMap ? stockMap.get(symbol) : null;
            const isPreIpo = preIpoSymbols && preIpoSymbols.has(symbol);
            if (stock && !isPreIpo && stock.changePct !== null && stock.changePct !== undefined) {
                return false;
            }
        }
        return !node.direction || node.direction === 'neutral';
    };

    window.NewsGraph.getNodeColor = function(node, stockMap, preIpoSymbols) {
        const isSphere = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
        if (!isSphere) {
            const symbol = (node.label || node.id || '').split(' - ')[0].trim().toUpperCase();
            const stock = stockMap ? stockMap.get(symbol) : null;
            const isPreIpo = preIpoSymbols && preIpoSymbols.has(symbol);
            if (stock && !isPreIpo && stock.changePct !== null && stock.changePct !== undefined) {
                const chgPct = stock.changePct;
                if (chgPct >= 1) {
                    return '#00ff00';
                } else if (chgPct <= -1) {
                    return '#ff0000';
                } else {
                    return '#ffff00';
                }
            }
        }
        const baseColorHex = window.NewsGraph.getColor(node.direction);
        return '#' + baseColorHex.toString(16).padStart(6, '0');
    };

    window.NewsGraph.isConeFlipped = function(node, stockMap, preIpoSymbols) {
        if (node.type === 'ticker' || node.type === 'ticker_group') {
            const symbol = (node.label || node.id || '').split(' - ')[0].trim().toUpperCase();
            const stock = stockMap ? stockMap.get(symbol) : null;
            const isPreIpo = preIpoSymbols && preIpoSymbols.has(symbol);
            return !!(stock && !isPreIpo && stock.changePct !== null && stock.changePct !== undefined && stock.changePct < 0);
        }
        return false;
    };

    window.NewsGraph.getNodeMaterialConfig = function(node, stockMap, preIpoSymbols, isSelected, isHovered, isVR) {
        const isSphere = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
        const isDimmed = !isSphere && !isHovered && !isSelected;
        const isNeutral = window.NewsGraph.isNodeNeutral(node, stockMap, preIpoSymbols);
        const isFresh = node.freshness && (node.freshness.status === 'inflection' || node.freshness.status === 'new');

        const opacity = isSphere ? 0.85 : 0.65;

        const baseColor = window.NewsGraph.getNodeColor(node, stockMap, preIpoSymbols);

        const darkenHex = (hex, factor) => {
            let r = parseInt(hex.slice(1, 3), 16);
            let g = parseInt(hex.slice(3, 5), 16);
            let b = parseInt(hex.slice(5, 7), 16);
            r = Math.round(r * factor);
            g = Math.round(g * factor);
            b = Math.round(b * factor);
            return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
        };

        const activeColor = baseColor;
        const nodeColor = isFresh ? darkenHex(activeColor, 0.4) : activeColor;

        if (isDimmed) {
            const dimmedColor = darkenHex(nodeColor, 0.7);
            return {
                color: dimmedColor,
                opacity: opacity * 0.7,
                metalness: (isNeutral && !isFresh) ? 0.7 : 0.0,
                roughness: (isNeutral && !isFresh) ? 0.3 : 0.5,
                depthTest: true,
                depthWrite: true,
                emissive: isFresh ? (isNeutral ? '#ccccdd' : baseColor) : (isNeutral ? '#666677' : '#000000'),
                emissiveIntensity: isFresh ? 0.05 : (isNeutral ? 0.1 : 0.0)
            };
        } else {
            return {
                color: nodeColor,
                opacity: opacity,
                metalness: (isNeutral && !isFresh) ? 0.7 : 0.0,
                roughness: (isNeutral && !isFresh) ? 0.3 : 0.5,
                depthTest: true,
                depthWrite: true,
                emissive: isFresh ? (isNeutral ? '#ccccdd' : baseColor) : (isNeutral ? '#ccccdd' : '#000000'),
                emissiveIntensity: isFresh ? 0.05 : (isNeutral ? 0.2 : 0.0)
            };
        }
    };

    window.NewsGraph.updateSelectionOutline = function(el, isSphere, r, isSelected, materialOverride) {
        if (isSelected) {
            let outlineEl = el.querySelector('.selection-outline');
            if (!outlineEl) {
                outlineEl = document.createElement('a-entity');
                outlineEl.classList.add('selection-outline');
                const outlineOffset = r * 0.2;
                if (isSphere) {
                    outlineEl.setAttribute('geometry', `primitive: sphere; radius: ${r + outlineOffset}; segmentsWidth: 20; segmentsHeight: 14`);
                } else {
                    outlineEl.setAttribute('geometry', `primitive: cone; radiusBottom: ${r + outlineOffset}; radiusTop: 0; height: ${(r + outlineOffset) * 2}; segmentsRadial: 24; segmentsHeight: 1`);
                }
                const defaultMat = {
                    color: '#00ffff',
                    side: 'back',
                    transparent: true,
                    opacity: 0.5,
                    shader: 'flat',
                    depthWrite: false
                };
                const matConfig = Object.assign({}, defaultMat, materialOverride);
                outlineEl.setAttribute('material', matConfig);
                el.appendChild(outlineEl);
            }
        } else {
            const outlineEl = el.querySelector('.selection-outline');
            if (outlineEl) {
                outlineEl.remove();
            }
        }
    };

    window.NewsGraph.calculateLuminance = function (color) {
        const THREE = window.THREE || (window.AFRAME && window.AFRAME.THREE);
        if (!THREE) return 0.5;
        const colorObj = new THREE.Color(color);
        return 0.2126 * colorObj.r + 0.7152 * colorObj.g + 0.0722 * colorObj.b;
    };

    window.NewsGraph.createGlowTexture = function() {
        const THREE = window.THREE || (window.AFRAME && window.AFRAME.THREE);
        if (!THREE) return null;
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 128;
        const context = canvas.getContext('2d');
        const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
        gradient.addColorStop(0, 'rgba(255,255,255,1)');
        gradient.addColorStop(0.3, 'rgba(255,255,255,0.6)');
        gradient.addColorStop(1, 'rgba(255,255,255,0)');
        context.fillStyle = gradient;
        context.fillRect(0, 0, 128, 128);
        const texture = new THREE.Texture(canvas);
        texture.needsUpdate = true;
        return texture;
    };

    window.NewsGraph.updateGlowPulse = function(elements, time, defaultMin = 1.01, defaultMax = 1.08) {
        const period = 2000;
        const t = time % period;
        const phase = t / period;
        const wave = 0.5 * (1 - Math.cos(Math.PI * 2 * phase));

        elements.forEach(el => {
            if (el && el.userData && el.userData.isFresh) {
                const min = el.userData.emissiveMin !== undefined ? el.userData.emissiveMin : defaultMin;
                const max = el.userData.emissiveMax !== undefined ? el.userData.emissiveMax : defaultMax;
                const intensity = min + (max - min) * wave;

                const mesh = el.getObject3D ? el.getObject3D('mesh') : null;
                if (mesh && mesh.material) {
                    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                    materials.forEach(mat => {
                        if (mat && 'emissiveIntensity' in mat) {
                            mat.emissiveIntensity = intensity;
                        }
                    });
                }

                const glowSprite = el.getObject3D ? el.getObject3D('glowSprite') : null;
                if (glowSprite && el.userData.baseGlowScale) {
                    const glowMaxOp = (el.userData && el.userData.isDimmed) ? 0.15 : 0.8;
                    glowSprite.material.opacity = wave * glowMaxOp;
                    const scale = el.userData.baseGlowScale * (1.0 + wave * 0.2);
                    glowSprite.scale.set(scale, scale, 1);
                }
            }
        });
    };

    window.NewsGraph.buildRelatedTopicsData = function(node, data) {
        if (!node || !data) return [];

        const relationships = data.relationships || [];
        const narrativeMap = {};
        (data.narratives || []).forEach(n => { narrativeMap[n.id] = n; });
        const tickerMap = {};
        (data.tickers || []).forEach(t => { tickerMap[t.id] = t; });

        const connectedRels = relationships.filter(r =>
            (r.source_id === node.id || r.target_id === node.id) &&
            r.relationship !== 'offsets'
        );

        const entries = [];
        const seen = new Set();

        const NARRATIVE_TYPES = window.NewsGraph.NARRATIVE_TYPES;
        const nodeIsNarrative = NARRATIVE_TYPES.has(node.type);

        connectedRels.forEach(r => {
            const otherId = r.source_id === node.id ? r.target_id : r.source_id;
            const otherType = r.source_id === node.id ? r.target_type : r.source_type;

            const otherIsNarrative = NARRATIVE_TYPES.has(otherType);
            const otherIsTicker = otherType === 'ticker' || otherType === 'ticker_group';

            if (!otherIsNarrative && !otherIsTicker) return;
            if (!nodeIsNarrative && otherIsTicker) return;

            if (seen.has(otherId)) return;
            seen.add(otherId);

            let item = null;
            const isTicker = otherIsTicker;
            if (isTicker) {
                item = tickerMap[otherId];
            } else {
                item = narrativeMap[otherId];
            }
            if (!item) return;

            entries.push({ item, isTicker, rel: r.relationship || '', strength: r.strength || 0 });
        });

        entries.sort((a, b) => b.strength - a.strength);
        return entries;
    };
})();
